import React, { useState, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';

import { useI18n } from '~Hooks';
import Item from './Components/Item';
import BottomNavigation from './Components/BottomNavigation';
import type { Choice, Order, SelectedUpsell, LocalUpsell } from '../..';
import type { LocalItem } from '~Types';

const Wrapper = styled.div<{ show: boolean }>`
  padding: 56px 164px 0px;
  height: 100%;
  opacity: ${({ show }) => (show ? 1 : 0)};
  transition: 0.3s opacity;
  overflow: hidden;

  @media (orientation: landscape) {
    padding: 40px 60px 0;
  }
`;
const Title = styled.h2`
  color: rgb(19, 19, 22);
  font-family: Eina01-SemiBold;
  font-size: 64px;
  font-weight: 600;
  letter-spacing: 0px;
  line-height: 72px;
  margin: 0 0 56px;
`;
const List = styled.div`
  overflow: auto;
  padding-bottom: 800px;
  height: 100%;

  ::-webkit-scrollbar {
    display: none; /* for Chrome, Safari, and Opera */
  }

  @media (orientation: landscape) {
    padding-bottom: 600px;
  }
`;

export type Choices = {
  [key: string]: Choice & {
    count: number;
  };
};
interface Props {
  disabled: boolean;
  upsells: LocalUpsell[];
  toDetailScreen: () => void;
  order: Order;
  onSubmit: () => void;
  choicesArr: Choice[];
  handleSetChoice: (currentStep: number, choice: SelectedUpsell | string, enableMultiSelect: boolean) => void;
  price: number;
  cartItems: LocalItem[];
}
const Upsells = ({
  disabled,
  upsells,
  toDetailScreen,
  onSubmit,
  order,
  choicesArr,
  handleSetChoice,
  price,
  cartItems,
}: Props) => {
  const { i18n } = useI18n();
  const [step, setStep] = useState<number>(0);
  const [show, setShow] = useState<boolean>(false);

  const upsale = upsells[step];
  const { name, items } = upsale || {};
  const nextButtonTitle = i18n(
    step < upsells.length - 1 ? 'SSK.ItemUpsells.Next' : 'SSK.ItemUpsells.Complete',
  );

  let timeout: number;
  let animationTimeout: number;

  const choices: Choices = useMemo(
    () =>
      choicesArr.reduce(
        (acc: Choices, cur: Choice) => ({
          ...acc,
          [cur.id]: {
            count: (acc[cur.id]?.count || 0) + 1,
            ...cur,
          },
        }),
        {},
      ),
    [choicesArr],
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setShow(true);
    }, 100);
    return () => {
      clearTimeout(timeout);
      clearTimeout(t);
      clearTimeout(animationTimeout);
    };
  }, []);

  const stepAnimation = async (callback: () => void, disabled?: boolean) => {
    if (disabled) {
      callback();
      return;
    }

    setShow(false);
    animationTimeout = setTimeout(() => {
      callback();
      setShow(true);
    }, 300);
  };

  const changeStep = (chosenStep: number) => {
    if (chosenStep < 0) {
      toDetailScreen();
    } else {
      stepAnimation(() => setStep(chosenStep));
    }
  };

  const actionNextButton = () => {
    if (step === upsells.length - 1) {
      onSubmit();
    } else {
      changeStep(step + 1);
    }
  };

  const addUpsellItem = (choice: SelectedUpsell) => {
    if (disabled) {
      return;
    }
    
    handleSetChoice(choice.currentStep, choice, upsale.enableMultiSelect);
  
    if (!upsale.enableMultiSelect) {
      timeout = setTimeout(() => {
        const next = step < upsells.length - 1;
        stepAnimation(() => (next ? setStep(step + 1) : null), !next);
      }, 200);
    }
  };
  
  const removeUpsellItem = (id: string) => {
    handleSetChoice(step, id, true);
  };

  if (!upsale) {
    return null;
  }

  return (
    <Wrapper show={show}>
      <Title>{i18n(name)}</Title>
      <List>
        {items.map((upsellItem) => {
          return (
            <Item
              key={upsellItem.id}
              {...upsellItem}
              choices={choices}
              onAddUpsellItem={() =>
                addUpsellItem({
                  currentStep: step,
                  id: upsellItem.id,
                  name: i18n(upsellItem.titleLang),
                  price: upsellItem.price,
                })
              }
              onRemoveUpsellItem={() => removeUpsellItem(upsellItem.id)}
              cartItems={cartItems}
              enableMultiSelect={upsale.enableMultiSelect}
            />
          );
        })}
      </List>
      <BottomNavigation
        step={step}
        upsells={upsells}
        changeStep={changeStep}
        order={order}
        title={nextButtonTitle}
        actionNextButton={actionNextButton}
        onRemoveUpsellItem={removeUpsellItem}
        price={price}
        choicesArr={choicesArr}
      />
    </Wrapper>
  );
};

export default Upsells;
